import type { ReactNode } from "react";

import type { RecordDetail as Record } from "../record-detail.ts";
import { detailActionFor } from "../record-detail.ts";
import { StateBadge } from "./StateBadge.tsx";

/**
 * The selected row of a board, in full.
 *
 * The right-hand column at 1240 and above, and an overlay over the table below it — the design's
 * own breakpoint, and the reason this is a component rather than a column: at 1024 it is the same
 * panel sliding over the same table, not a second layout.
 *
 * It uses the same `.detail__*` vocabulary as the dispatch console's panel, and is a copy of it
 * rather than a shared import: the two consoles are separate applications with separate dependency
 * graphs, and a package existing to hold one component would couple their release cycles to save a
 * file. The duplication is small, visible and deliberate; if a third console needs it, that is the
 * point at which it becomes a package.
 *
 * ## Why it closes on Escape and returns focus
 *
 * Below 1240 it covers the table, so a keyboard user who opened a row has their focus inside
 * something they cannot see around. Escape closes it and focus goes back to the row that opened it,
 * which the board owns — this component reports the intent and does not reach for the DOM itself.
 */

export interface RecordDetailProps {
  readonly record: Record;
  /** Closes the panel. Below the design's 1240 breakpoint this is the only way out of the overlay. */
  readonly onClose: () => void;
  /** True while the panel is the overlay rather than the column, which is what `--open` styles. */
  readonly open: boolean;
}

export function RecordDetail({ record, onClose, open }: RecordDetailProps): ReactNode {
  /*
   * The footer's action, from the row's own state class.
   *
   * Derived rather than passed, so a board cannot draw a different button from the one the design
   * draws for the state it is already showing — the same reason the badge travels with the record
   * instead of being computed twice.
   */
  const action = record.state === undefined ? undefined : detailActionFor(record.state.classKey);

  return (
    <aside
      className={open ? "detail detail--open" : "detail"}
      aria-label={`${record.headline} — details`}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="detail__head">
        {/*
          The design puts a back control here only in the overlay, where the panel covers the table.
          It is rendered at every width and hidden by the stylesheet below its own breakpoint,
          because a control that exists only at one width is a control a keyboard user loses when
          the window is resized under them.
        */}
        <button type="button" className="detail__back" onClick={onClose} aria-label="Close details">
          <span aria-hidden="true">arrow_back</span>
        </button>
        <span className="detail__ident">
          <span className="detail__headline">{record.headline}</span>
          <span className="detail__source">{record.source}</span>
        </span>
        {record.state === undefined ? null : (
          <StateBadge
            code={record.state.code}
            status={record.state.status}
            classKey={record.state.classKey}
            label={record.state.label}
          />
        )}
      </div>

      <div className="detail__body">
        <div className="detail__fields">
          {/*
            The identifier is first and always present. Every other line is what the board's own
            operation answered, so a panel never asks for anything the row did not already carry.
          */}
          <div className="detail__field">
            <span className="detail__field-name">Identifier</span>
            <span className="detail__field-value detail__field-value--mono">
              {record.identifier}
            </span>
          </div>
          {record.fields.map((entry) => (
            <div className="detail__field" key={entry.name}>
              <span className="detail__field-name">{entry.name}</span>
              <span
                className={
                  entry.absent === true
                    ? "detail__field-value detail__field-value--absent"
                    : entry.mono === true
                      ? "detail__field-value detail__field-value--mono"
                      : "detail__field-value"
                }
                {...(entry.title === undefined ? {} : { title: entry.title })}
              >
                {entry.value}
              </span>
            </div>
          ))}
        </div>
        {record.note === undefined ? null : <p className="detail__footnote">{record.note}</p>}
      </div>

      {/*
        The design's footer: Close, and one state-dependent action beside it.

        This is the one place this console's detail panel differs from the finance console's, and
        it differs because the design file does. `20 WP19 Support Console.dc.html` carries an
        `act` table keyed on the state class and sets `display: act ? 'inline-flex' : 'none'`, so
        seven of the ten classes draw a button and three draw none. `detailActionFor` holds the
        table; every one of the seven is refused, with the reason on the button, because every one
        of them names a command this platform does not have. See the note on that function.

        Close is unconditional, as it is in the design, and is a second way out of the panel beside
        the back arrow in the head — which is what the design draws at the width where the panel is
        a column rather than an overlay.
      */}
      <div className="detail__foot">
        <button type="button" className="detail__close" onClick={onClose}>
          Close
        </button>
        {action === undefined ? null : (
          <button type="button" className="detail__action" disabled title={action.refusedBecause}>
            <span aria-hidden="true" className="detail__action-glyph">
              {action.glyph}
            </span>
            {action.label}
          </button>
        )}
      </div>
    </aside>
  );
}
